const DevilryGarmentSizing = {
  alias(value) {
    const label = String(value ?? '').trim().toUpperCase().replace(/[–—]/g, '-')
      .replace(/^(ADULT|TODDLER|BABY) - /, '');
    return ({ SMALL: 'S', MEDIUM: 'M', LARGE: 'L', '2': '2T', '3': '3T', '4': '4T',
      '5/6': '5/6T', '5-6T': '5/6T' })[label] || label;
  },
  state(config, selected, chartKey = null) {
    const browsing = config.key === 'mixed' && config.mappings.find((mapping) => mapping.key === chartKey);
    if (browsing) {
      selected = [...selected];
      selected[config.garmentIndex] = browsing.value;
      selected[config.sizeIndex] = null;
    }
    const key = config.key === 'mixed'
      ? config.mappings.find((mapping) => mapping.value === selected[config.garmentIndex])?.key
      : config.key;
    const profile = config.profiles[key];
    if (!profile) return { profile: null, rows: [], available: false, uncovered: false };
    const variants = config.variants.filter((variant) => variant.options.length === selected.length &&
      variant.options.every((value, index) => index === config.sizeIndex || value === selected[index]));
    const offered = new Set(config.complete ? variants.map((v) => this.alias(v.options[config.sizeIndex])) : config.offered);
    const available = new Set(config.complete ? variants.filter((v) => v.available).map((v) => this.alias(v.options[config.sizeIndex])) : []);
    const offeredRows = profile.rows.filter((row) => offered.has(row.size));
    const availableRows = offeredRows.filter((row) => available.has(row.size));
    const rows = availableRows.length ? availableRows : offeredRows;
    const selectedSize = this.alias(selected[config.sizeIndex]);
    return { profile, rows, offeredRows, availableRows, available: availableRows.length > 0,
      unknownAvailability: !config.complete,
      uncovered: Boolean(selectedSize && !profile.rows.some((row) => row.size === selectedSize)) };
  },
  match(config, selected, values, unit) {
    const keys = ['g18500', 'm2580', 'g18000', 'm2480', 'as4062', 'bc3480', 'co200', 'g18500b'];
    const state = this.state(config, selected);
    const count = state.profile?.columns.length;
    if (!keys.includes(config.key) || !config.fitEnabled || !config.complete || !state.profile || state.uncovered) return { kind: 'none', rows: [] };
    if (!['cm', 'in'].includes(unit) || values.length !== count || values.some(v => !Number.isFinite(v) || v <= 0)) return { kind: 'invalid', rows: [] };
    if (!state.offeredRows.length || !state.availableRows.length) return { kind: 'none', rows: [] };
    const rounding = unit === 'cm' ? .127 : .05;
    const nearest = values.map((value, index) => {
      const cells = state.offeredRows.map(row => row[unit][index]);
      if (value < Math.min(...cells) - rounding || value > Math.max(...cells) + rounding) return null;
      const distance = Math.min(...cells.map(cell => Math.abs(cell - value)));
      return new Set(state.offeredRows.filter(row => Math.abs(row[unit][index] - value) <= distance + rounding).map(row => row.size));
    });
    if (nearest.some(set => set === null)) return { kind: 'outside', rows: [] };
    const consistent = state.offeredRows.filter(row => nearest.every(set => set.has(row.size)));
    if (!consistent.length) return { kind: 'conflict', rows: [] };
    const rows = consistent.filter(row => state.availableRows.some(available => available.size === row.size));
    if (!rows.length) return { kind: 'none', rows: [] };
    return { kind: rows.length === 1 ? 'closest' : 'ambiguous', rows };
  },
};

if (typeof module !== 'undefined') module.exports = DevilryGarmentSizing;

if (typeof customElements !== 'undefined' && !customElements.get('devilry-garment-guide')) {
  customElements.define('devilry-garment-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.config = JSON.parse(this.querySelector('[data-dg-config]').textContent);
      this.controls = new AbortController();
      const options = { signal: this.controls.signal };
      this.chartKey = null;
      this.chartSelector = this.querySelector('[data-dg-select]');
      if (this.chartSelector && this.hasAttribute('data-modal') && typeof HTMLDialogElement !== 'undefined') {
        this.querySelector('[data-dg-selector]').hidden = false;
        this.chartSelector.addEventListener('change', () => {
          this.chartKey = this.chartSelector.value;
          this.update();
        }, options);
      }
      if (this.hasAttribute('data-modal') && typeof HTMLDialogElement !== 'undefined') {
        this.details = this.querySelector('details.df-fallback');
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'df-dialog dg-dialog';
        this.dialog.id = `GarmentDialog-${this.dataset.section}`;
        this.dialog.setAttribute('aria-labelledby', `GarmentHeading-${this.dataset.section}`);
        this.dialog.append(this.querySelector('[data-dg-content]'));
        this.append(this.dialog);
        this.details.hidden = true;
        this.querySelector('[data-dg-close]').hidden = false;
        this.root.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-dg-open]');
          if (!trigger || trigger.dataset.dgOpen !== this.dataset.section) return;
          event.preventDefault();
          this.trigger = trigger;
          trigger.setAttribute('aria-controls', this.dialog.id);
          trigger.setAttribute('aria-haspopup', 'dialog');
          this.chartKey = null;
          this.update();
          this.showFitTab('find');
          if (this.dialog.open) return;
          this.querySelector('.df-measuring').open = false;
          this.savedOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          this.dialog.showModal();
          (this.querySelector('[data-dg-tab="find"]') || this.chartSelector || this.querySelector('.dc-unit:checked'))?.focus();
        }, options);
        this.querySelector('[data-dg-close]').addEventListener('click', () => this.dialog.close(), options);
        this.dialog.addEventListener('close', () => {
          document.documentElement.style.overflow = this.savedOverflow ?? '';
          const trigger = this.trigger?.isConnected ? this.trigger : this.root.querySelector('[data-dg-open]');
          trigger?.focus();
        }, options);
        this.dialog.addEventListener('keydown', (event) => {
          const tab = event.target.closest('[data-dg-tab]');
          if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const name = event.key === 'Home' ? 'find' : event.key === 'End' ? 'chart' : tab.dataset.dgTab === 'find' ? 'chart' : 'find';
            this.showFitTab(name);
            this.querySelector(`[data-dg-tab="${name}"]`).focus();
          }
          if (event.key !== 'Tab') return;
          const focusable = [...this.dialog.querySelectorAll('button, select, input:not([type="radio"]), input:checked, summary, [tabindex="0"]')]
            .filter((el) => !el.disabled && !el.closest('[hidden]') && el.getClientRects().length);
          const first = focusable[0], last = focusable.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }, options);
        this.initializeFit(options);
      }
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) { this.chartKey = null; this.fitPending = true; this.clearFitResult(); this.update(); }
      }, options);
      this.pickerObserver = new MutationObserver((records) => {
        if (records.some((record) => record.target.closest?.('variant-selects') ||
          [...record.addedNodes, ...record.removedNodes].some((node) => node.matches?.('variant-selects')))) this.update();
      });
      this.pickerObserver.observe(this.root, { childList: true, subtree: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const selector = this.hasAttribute('data-modal') ? 'devilry-garment-guide[data-modal]' : 'devilry-garment-guide:not([data-modal])';
          const fresh = data.html?.querySelector(`${selector} [data-dg-config]`);
          if (fresh) { this.config = JSON.parse(fresh.textContent); this.fitPending = false; }
          this.update();
        });
      }
      this.update();
      this.initialized = true;
    }
    disconnectedCallback() {
      this.controls?.abort();
      this.unsubscribe?.();
      this.pickerObserver?.disconnect();
      if (this.dialog) {
        if (this.dialog.open) {
          this.dialog.close();
          document.documentElement.style.overflow = this.savedOverflow ?? '';
        }
        this.details.append(this.dialog.querySelector('[data-dg-content]'));
        this.dialog.remove();
      }
      this.initialized = false;
    }
    selected() {
      const picker = [...this.root.querySelectorAll('variant-selects')].find((element) => !element.hidden && element.style.display !== 'none');
      if (!picker) return this.config.selected;
      return [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')].map((group) =>
        group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value);
    }
    initializeFit(options) {
      const finder = this.querySelector('[data-dg-finder]');
      if (!finder || !this.config.fitEnabled) return;
      finder.hidden = false;
      this.fitUnit = this.querySelector('[data-dg-fit-units]').value;
      const chart = this.querySelector('[data-dg-table-panel]');
      chart.setAttribute('role', 'tabpanel');
      chart.setAttribute('aria-labelledby', this.querySelector('[data-dg-tab="chart"]').id);
      this.dialog.querySelector('[data-dg-content]').append(this.querySelector('.df-measuring'));
      this.querySelectorAll('[data-dg-tab]').forEach(tab => tab.addEventListener('click', () => this.showFitTab(tab.dataset.dgTab), options));
      this.querySelector('[data-dg-fit-units]').addEventListener('change', event => {
        const next = event.target.value;
        this.querySelectorAll('[data-dg-fit-value]').forEach(input => {
          if (input.value !== '' && Number.isFinite(input.valueAsNumber)) input.value = Number((this.fitUnit === 'in' ? input.valueAsNumber * 2.54 : input.valueAsNumber / 2.54).toFixed(2));
        });
        this.fitUnit = next;
        this.updateFitUnits();
        this.clearFitResult();
      }, options);
      this.querySelectorAll('[data-dg-fit-value]').forEach(input => input.addEventListener('input', () => this.clearFitResult(), options));
      this.querySelector('[data-dg-fit-form]').addEventListener('submit', event => {
        event.preventDefault(); event.stopPropagation(); this.calculateFit();
      }, options);
      this.updateFitUnits();
      this.showFitTab('find');
    }
    showFitTab(name) {
      if (!this.querySelector('[data-dg-finder]')) return;
      this.querySelectorAll('[data-dg-tab]').forEach(tab => {
        const active = tab.dataset.dgTab === name;
        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
      });
      this.querySelector('[data-dg-fit-panel]').hidden = name !== 'find';
      this.querySelector('[data-dg-table-panel]').hidden = name !== 'chart';
    }
    updateFitUnits() {
      this.querySelectorAll('[data-dg-fit-unit-label]').forEach(label => { label.textContent = `(${this.config.fitStrings[this.fitUnit]})`; });
      this.querySelectorAll('.dc-unit').forEach(input => { input.checked = input.value === this.fitUnit; });
    }
    clearFitResult() {
      this.querySelector('[data-dg-fit-result]')?.replaceChildren();
      this.querySelectorAll('[data-dg-fit-value]').forEach(input => input.removeAttribute('aria-invalid'));
    }
    calculateFit() {
      const result = this.querySelector('[data-dg-fit-result]');
      this.clearFitResult();
      const inputs = [...this.querySelectorAll('[data-dg-fit-value]')];
      const state = DevilryGarmentSizing.state(this.config, this.selected());
      const match = this.fitPending ? { kind: 'pending', rows: [] } : DevilryGarmentSizing.match(this.config, this.selected(), inputs.map(input => input.valueAsNumber), this.fitUnit);
      const heading = document.createElement('p'); heading.className = 'df-result-heading'; heading.textContent = this.config.fitStrings[match.kind]; result.append(heading);
      if (match.kind === 'invalid') {
        inputs.filter(input => !Number.isFinite(input.valueAsNumber) || input.valueAsNumber <= 0).forEach(input => input.setAttribute('aria-invalid', 'true'));
        inputs.find(input => input.hasAttribute('aria-invalid'))?.focus();
      }
      match.rows.forEach(row => {
        const label = document.createElement('p'), strong = document.createElement('strong');
        label.className = 'df-result-option'; strong.textContent = row.size; label.append(strong); result.append(label);
        const difference = document.createElement('p'); difference.className = 'df-comparison';
        difference.textContent = this.config.fitStrings.comparison + ': ' + state.profile.columns.map((column, index) => {
          const delta = Number((row[this.fitUnit][index] - inputs[index].valueAsNumber).toFixed(2));
          return `${column} ${delta > 0 ? '+' : ''}${delta} ${this.config.fitStrings[this.fitUnit]}`;
        }).join('; ');
        result.append(difference);
      });
    }
    update() {
      this.clearFitResult();
      const selected = this.selected();
      const state = DevilryGarmentSizing.state(this.config, selected, this.chartKey);
      const profile = state.profile;
      if (this.chartSelector && profile) this.chartSelector.value = profile.key;
      this.querySelector('[data-dg-name]').textContent = profile?.name || selected[this.config.garmentIndex] || this.config.strings.choose_garment;
      this.querySelector('[data-dg-chart]').hidden = !profile;
      this.querySelector('[data-dg-fallback]').hidden = Boolean(profile);
      if (!profile) return;
      this.querySelectorAll('[data-dg-columns]').forEach((head) => {
        head.replaceChildren();
        [this.config.strings.size, ...profile.columns].forEach((label) => {
          const th = document.createElement('th'); th.scope = 'col'; th.textContent = label; head.append(th);
        });
      });
      this.querySelectorAll('[data-dg-rows]').forEach((body) => {
        body.replaceChildren();
        state.rows.forEach((row) => {
          const tr = document.createElement('tr'), th = document.createElement('th');
          th.scope = 'row'; th.textContent = row.size;
          if (!state.available && !state.unknownAvailability) th.textContent += ` — ${this.config.strings.unavailable}`;
          tr.append(th);
          row[body.dataset.dgRows].forEach((value) => { const td = document.createElement('td'); td.textContent = value; tr.append(td); });
          body.append(tr);
        });
      });
      this.querySelector('[data-dg-status]').textContent = state.uncovered ? this.config.strings.not_covered
        : !state.available && !state.unknownAvailability ? this.config.strings.no_available : '';
      const education = this.querySelector('[data-dg-education]');
      if (education.dataset.profile !== profile.key) {
        const template = [...this.querySelectorAll('[data-dg-education-template]')].find((element) => element.dataset.dgEducationTemplate === profile.key);
        education.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
        education.dataset.profile = profile.key;
      }
      this.querySelectorAll('[data-dg-notices], [data-dg-help-notices], [data-dg-fit-notices]').forEach((notices) => {
        notices.replaceChildren();
        (profile.notices || []).filter(Boolean).forEach((text) => { const p = document.createElement('p'); p.textContent = text; notices.append(p); });
      });
    }
  });
}
